import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import type { AdminUser, ClientProfile, Me, Role } from '../lib/api';
import { fmtDate, titleCase } from '../lib/format';
import { Empty, Kpi, PageHead, Panel, Pill } from '../components/ui';
import { AccountPanel } from '../components/AccountPanel';

const ROLE_HELP: Record<Role, string> = {
  owner: 'Everything, including members and profile removal',
  analyst: 'Create and edit profiles, notes, decisions, acknowledge alerts',
  viewer: 'Read only',
  client: 'A customer login: sees only its own dashboard and opportunities, without NTN-internal notes',
};

export function Admin({ me }: { me: Me }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reveal, setReveal] = useState<{ email: string; password: string; existing?: boolean } | null>(null);
  const [inv, setInv] = useState({ email: '', display_name: '', role: 'analyst' as Role, client_profile_id: '' });
  const [profiles, setProfiles] = useState<ClientProfile[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api<{ items: AdminUser[] }>('/api/admin/users').then((r) => setUsers(r.items)).catch((e: Error) => setErr(e.message));
  }, []);
  useEffect(() => {
    if (me.role !== 'owner') return;
    load();
    api<{ items: ClientProfile[] }>('/api/clients').then((r) => setProfiles(r.items)).catch(() => setProfiles([]));
  }, [load, me.role]);

  async function invite(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await api<{ email: string; temp_password?: string; existing_account: boolean }>('/api/admin/users', {
        method: 'POST', json: { email: inv.email.trim(), display_name: inv.display_name.trim() || undefined, role: inv.role, client_profile_id: inv.role === 'client' ? inv.client_profile_id : undefined },
      });
      setReveal(r.temp_password ? { email: r.email, password: r.temp_password } : { email: r.email, password: '', existing: true });
      setInv({ email: '', display_name: '', role: 'analyst', client_profile_id: '' });
      load();
    } catch (ex) {
      setErr(ex instanceof ApiError && ex.status === 409 ? 'That person is already a member of this workspace.' : (ex as Error).message);
    } finally { setBusy(false); }
  }
  async function patch(u: AdminUser, body: { role?: Role; disabled?: boolean }) {
    setErr(null);
    try { await api(`/api/admin/users/${u.id}`, { method: 'PATCH', json: body }); load(); }
    catch (ex) {
      const code = ex instanceof ApiError ? ex.message : '';
      setErr(code === 'last_owner' ? 'A workspace needs at least one active owner.' : code === 'member_of_other_workspaces' ? 'This person belongs to other workspaces; disable them from the platform instead.' : code === 'cannot_change_self' ? 'You cannot change your own role or status.' : (ex as Error).message);
    }
  }
  async function reset(u: AdminUser) {
    if (!confirm(`Reset the password for ${u.email}? Their current password stops working immediately.`)) return;
    try { const r = await api<{ temp_password: string }>(`/api/admin/users/${u.id}/reset-password`, { method: 'POST' }); setReveal({ email: u.email, password: r.temp_password }); load(); }
    catch (ex) { setErr((ex as Error).message); }
  }
  const account = <AccountPanel me={me} />;

  if (me.role !== 'owner') {
    return <><PageHead title="Admin" sub="Workspace members and your account" />{account}<Empty>Only workspace owners can manage members.</Empty></>;
  }

  const list = users ?? [];
  return (
    <>
      <PageHead title="Admin" sub={`Members of the ${me.tenant.name} workspace, roles and access`} />
      {err && <div className="error-text" style={{ marginBottom: 12 }}>{err}</div>}
      <div className="kpi-row">
        <Kpi label="Members" value={users ? list.length : '…'} />
        <Kpi label="Owners" value={list.filter((u) => u.role === 'owner' && !u.disabled_at).length} />
        <Kpi label="Analysts" value={list.filter((u) => u.role === 'analyst' && !u.disabled_at).length} />
        <Kpi label="Viewers" value={list.filter((u) => u.role === 'viewer' && !u.disabled_at).length} />
        <Kpi label="Client logins" value={list.filter((u) => u.role === 'client' && !u.disabled_at).length} />
        <Kpi label="Awaiting first sign-in" value={list.filter((u) => u.must_change_password).length} />
      </div>

      {reveal && (
        <div className="reveal">
          {reveal.existing
            ? <>{reveal.email} already had a Praxis account and has been added to this workspace with their existing password.</>
            : <>Temporary password for <strong>{reveal.email}</strong>: <strong>{reveal.password}</strong><br /><span className="muted">Shown once. They must change it at first sign-in. Pass it on securely; Praxis does not send email.</span></>}
          <div style={{ marginTop: 8 }}><button className="ghost-btn" onClick={() => setReveal(null)}>Dismiss</button></div>
        </div>
      )}

      <div className="grid grid-2">
        <Panel title="Members">
          {users === null ? <Empty>Loading…</Empty> : (
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>Member</th><th>Role</th><th>Status</th><th>Since</th><th></th></tr></thead>
                <tbody>
                  {list.map((u) => {
                    const self = u.id === me.user.id;
                    return (
                      <tr key={u.id}>
                        <td><strong>{u.display_name}</strong><div className="muted sm">{u.email}{self && ' · you'}</div></td>
                        <td>
                          {self ? titleCase(u.role) : u.role === 'client' ? <>Client<div className="muted sm">{u.client_name ?? 'profile removed'}</div></> : (
                            <select className="select" style={{ width: 'auto', padding: '5px 28px 5px 9px' }} value={u.role} onChange={(e) => patch(u, { role: e.target.value as Role })}>
                              <option value="owner">Owner</option><option value="analyst">Analyst</option><option value="viewer">Viewer</option>
                            </select>
                          )}
                        </td>
                        <td>{u.disabled_at ? <Pill kind="muted">Disabled</Pill> : u.must_change_password ? <Pill kind="warn">Temp password</Pill> : <Pill kind="ok">Active</Pill>}</td>
                        <td className="muted">{fmtDate(u.member_since)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {!self && (
                            <>
                              <button className="filter-btn" onClick={() => reset(u)}>Reset password</button>{' '}
                              <button className="filter-btn" onClick={() => patch(u, { disabled: !u.disabled_at })}>{u.disabled_at ? 'Enable' : 'Disable'}</button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="Add a member">
          <form onSubmit={invite}>
            <div className="form-grid">
              <div className="field span-2"><label htmlFor="inv-email">Email</label><input id="inv-email" className="input" type="email" required value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} /></div>
              <div className="field"><label htmlFor="inv-name">Display name</label><input id="inv-name" className="input" value={inv.display_name} onChange={(e) => setInv({ ...inv, display_name: e.target.value })} /></div>
              <div className="field"><label htmlFor="inv-role">Role</label>
                <select id="inv-role" className="select" value={inv.role} onChange={(e) => setInv({ ...inv, role: e.target.value as Role })}>
                  <option value="owner">Owner</option><option value="analyst">Analyst</option><option value="viewer">Viewer</option><option value="client">Client</option>
                </select>
                <div className="muted sm">{ROLE_HELP[inv.role]}</div>
              </div>
              {inv.role === 'client' && (
                <div className="field span-2"><label htmlFor="inv-client">Client profile this login may see</label>
                  <select id="inv-client" className="select" required value={inv.client_profile_id} onChange={(e) => setInv({ ...inv, client_profile_id: e.target.value })}>
                    <option value="">Choose a client…</option>
                    {profiles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="form-actions"><button className="primary-btn" type="submit" disabled={busy || !inv.email.trim() || (inv.role === 'client' && !inv.client_profile_id)}>{busy ? 'Adding…' : 'Add member'}</button></div>
            <div className="muted sm">A temporary password is shown once after adding. There is no email in the prototype.</div>
          </form>
        </Panel>
      </div>
      {account}
    </>
  );
}
